import { Container, Modal } from '@cleartrip/ct-design-components'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
    fetchInterstitialData,
    isInterstitialEntryStale,
    selectInterstitialEntry
} from '@/redux/slices/interstitialCache'
import {
    addWowLoadingFareIds,
    fetchServicesData,
    getIsDynamicContentLoading,
    getSrpSearchData,
    getWowFares,
    mergeInterstitialData,
    mergeWowFares,
    removeWowLoadingFareIds,
    setCardIdToBenefitsMap,
    setCouponsData,
    setDynamicContentLoading,
    setServicesLoading
} from '@/redux/slices/srpSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store/hooks'

import { ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { setRumPage, trackRumEvent, RUM_EVENT } from '@/analytics/sparkloom'
import { getWowPricingSrpData } from '@/api/srp'
import { CabinMediaDrawer, getPeGalleryResourcesForCard, isVideoMediaUrl } from '@/components/common/CabinMediaDrawer'
import VdcEducationPage from '@/components/itinerary/Review/VdcBanner/VdcEducationPage/index'
import FareFamilyBlock from '@/components/srp/FareCards/FareFamilyBlock'
import PEInterstitialHeader from '@/components/srp/FareCards/FareFamilyModal/PEInterstitialHeader'
import PENudgeBanner from '@/components/srp/FareCards/FareFamilyModal/PENudgeBanner'
import {
    FlightUnavailableAlert,
    INTERSTITIAL_ALERT_TYPE,
    type InterstitialAlertType,
    PRICE_CHANGE_VARIANT,
    PriceChangeAlert,
    type PriceChangeVariant
} from '@/components/srp/FareCards/InterstitialAlerts'
import PerAdultTooltip from '@/components/srp/Tupple/Domestic/CommonComponents/PerAdultTooltip'
import WowFareCallout from '@/components/srp/Tupple/WowFareCallout'
import { CABIN_ORDER, cabinClassToLabel, CabinTabId, getCabinTabLabel, INDIGO_UPFRONT_CABIN, isIndigoUpfrontCabin } from '@/constants/flights/cabinClass'
import { CABIN_HERO_VIDEOS } from '@/constants/flights/peAssets'
import { INDIGO_CAPTION_CONFIG, isSrpThemeCabin, NUDGE_KIND, PE_DEFAULT_LOADING_SKELETON_COUNT, PE_INTRO_NUDGE_DURATION_MS, PE_INTRO_PHASE, PE_INTRO_TRANSITION_MS, PE_ITINERARY_UPGRADE_SEAT_IMAGE, PE_NUDGE_BANNER_COPY_NUDGE, PeIntroPhase, UPGRADE_NUDGE_VARIANT } from '@/constants/flights/premiumEconomy'
import usePremiumEconomyExperiment from '@/hooks/flights/usePremiumEconomyExperiment'
import useWowPricingExperiment from '@/hooks/srp/useWowPricingExperiment'
import useVariant from "@/hooks/statsig/useVariant"
import { Card, FareFamilyHeaderType, FareType, InterstitialData } from '@/types/srp'
import { formatDate, isFFFlowEnabled } from '@/utils/common'
import { filterFaresByCabin, getAvailableCabins, getCabinFareCount, getCabinThemeForTab, getCheapestFareForCabin, getCheapestFareForThemeCabin, getDefaultSelectedCabin, getFareCabin, getMinPriceByCabin, mergeCabinClassSummaries, resolveThemeCabin, resolveUpgradeCalloutTab } from '@/utils/flights/cabinTabs'
import { isNudgeSuppressed, onPremiumFlowInteraction } from '@/utils/flights/pe/frequency'
import { findFareWithUpgradeCallout, resolvePeUpgradeOffer } from '@/utils/flights/pe/media'
import { usePeNudgeGate } from '@/utils/flights/pe/usePeNudgeGate'
import { isEmpty, pluralize } from '@/utils/general/browserHelper'
import { getFormattedDate } from '@/utils/general/date'
import { isUserSignedIn } from '@/utils/general/user'
import useCreateItinerary from '@/utils/hooks/itinerary/useCreateItinerary'
import { persistIuItineraryCallout } from '@/utils/itinerary/upgradePE'
import { arrangeFaresArray, checkIsInterstitialDataPresent, getRavenPayloadCTBRAND, getSrpFareRequestPayload, isCTBrandedFareCardSelectedOnward, isCTBrandedFareCardSelectedReturn } from '@/utils/srp'
import { getBenefitProvided, getCouponDisplayAndDiscountValue, getFarePricingAttributes, getFaresInfo, getFFCookieValue, getFlightInfo, getNepDiscountAmount, getPtcFare, getSearchIntentDetails, getSpecialRTFareCase, getSpecialRtFaresInfo } from '@/utils/srp/helper'
import { buildInterstitialCacheKey } from '@/utils/srp/interstitialCacheKey'
import { buildInterstitialView } from '@/utils/srp/interstitialView'
import { ProcessedData } from '@/utils/srp/types'
import { extractInterstitialUserDialogue, mapUserDialogueToInterstitialAlert } from '@/utils/srp/userDialogue'
import { convertSelectedFlightsToSelectedCards, getFetchContentPayload } from '@/utils/srp/wowPayload'

import FareFamilyFooter from './Footer'
import FareFamilyHeader from './Header'

// Module-level dedup for interstitial WOW fetch — survives modal remounts,
// fast-refresh, double effects. Key: `${dataId}|${cardKey}`.
const interstitialWowFired = new Set<string>()

/** Must stay in sync with PEInterstitialHeader's top corners. */
const PE_MODAL_BORDER_RADIUS_PX = 12

type FareFamilyModalProps = {
    cardDetails: Card[]
    showPerAdultPricing: boolean
    isRT: boolean
    cardKey?: string
    tuppleIndex?: number
    processedData: Partial<ProcessedData>
    isFFFlow: boolean
    closeModal?: () => void
    isSpecialRT: boolean
}
const FareFamilyModal: React.FC<FareFamilyModalProps> = ({
    cardDetails,
    showPerAdultPricing,
    isRT,
    cardKey,
    tuppleIndex,
    processedData = {},
    isFFFlow,
    closeModal,
    isSpecialRT
}) => {
    const { isPeTestVariant, loading } = usePremiumEconomyExperiment()
    const getCardInfo = () => {
        return cardDetails.map(card => {
            const {
                airlineCode,
                airlineName,
                isMultiAirline,
                departureTime,
                arrivalTime,
                stops,
                totalDuration,
                from,
                to
            } = getFlightInfo(card, metaData) || {}

            const airlineDisplayName = isMultiAirline ? 'Multiple Airlines' : airlineName
            const formattedDepartureTime = getFormattedDate(departureTime, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
            const formattedArrivalTime = getFormattedDate(arrivalTime, 'HH:mm', 'YYYY-MM-DDTHH:mm:ss')
            const departureDate = getFormattedDate(departureTime, 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss')
            const [year, month, day] = departureDate.split('/')
            const formattedDate = formatDate(new Date(`${month}/${day}/${year}`))
            const flightDuration = `${totalDuration.hh ? `${totalDuration.hh}h` : ''} ${
                totalDuration.mm ? `${totalDuration.mm}m` : ''
            }`
            const stopsText = stops === 0 ? 'Non-stop' : pluralize(stops, 'stop')

            const faresObjectList: FareType[] = getFaresInfo(card, resolvedSubTravelOptions, resolvedFares) || []

            return {
                headerData: {
                    airlineCode,
                    airlineName: airlineDisplayName,
                    isMultiAirline,
                    from,
                    to,
                    departureTime: formattedDepartureTime,
                    arrivalTime: formattedArrivalTime,
                    duration: flightDuration,
                    stopsText,
                    formattedDate
                },
                faresObjectList
            }
        })
    }
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0)
    const [selectedCabinByLeg, setSelectedCabinByLeg] = useState<CabinTabId[]>(['ECONOMY', 'ECONOMY'])
    const selectedCabin = selectedCabinByLeg[selectedTabIndex] ?? 'ECONOMY'
    const cabinInitRef = useRef<boolean[]>([])
    // Leg the impression was last reported for
    const classImpressionLegRef = useRef<number | null>(null)
    const [showVdcEducation, setShowVdcEducation] = useState(false)
    const [selectedFares, setSelectedFares] = useState<FareType[]>([])
    const [selectedFareIndex, setSelectedFareIndex] = useState<number[]>([])

    const handleCabinSelect = (cabin: CabinTabId, legIndex: number = selectedTabIndex) => {
        setSelectedCabinByLeg(prev => {
            const next = [...prev]
            next[legIndex] = cabin
            return next
        })
    }

    // user cabin tab change on the SRP fare modal (a_class_selection).
    // Kept separate from handleCabinSelect so programmatic switches (e.g. the upgrade
    // CTA, which fires a_class_upgrade) don't also emit a selection event.
    //
    // Switching tabs only changes which cabin's cards are listed — it never selects,
    // pre-selects or clears a fare. The upgrade CTA (handlePeUpgrade) is the only path
    // that selects a fare in a cabin the user did not search for. Mirrors flights-pwa,
    // where `setSelectedClass` is independent of `selectedFares`.
    const handleCabinTabClick = (cabin: CabinTabId) => {
        ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, {
            action_name: 'a_class_selection',
            action_type: 'click',
            page_name: 'flights_srp',
            a_class_choose: getCabinTabLabel(cabin) || cabinClassToLabel[cabin as keyof typeof cabinClassToLabel] || cabin,
        })
        handleCabinSelect(cabin)
    }
    const [retryCountForServices, setRetryCountForServices] = useState<number>(0)
    const { searchData, servicesData, isServicesLoading, couponData, searchFareRefs } = useAppSelector(getSrpSearchData)
    const { cardIdToBenefitsMap , baggageAllowances} = servicesData
    const { dataId, fares, subTravelOptions, metaData, searchIntent, specialRTFareMap } = searchData
    const [interstitialFaresLoading, setInterstitialFaresLoading] = useState(false)
    const [interstitialCallComplete, setInterstitialCallComplete] = useState(false)
    const hasFiredSparkloom = useRef(false)
    const interstitialStartTimeRef = useRef<number>(Date.now())
    const isCfw = !!processedData?.isCfw

    // The request payload is built once and used for both the network call and
    // the cache key, so the key always describes exactly the request that would
    // be sent. Building them from separate inputs is how the previous key drifted
    // from the request it was supposed to identify.
    const interstitialPayload = useMemo(
        () =>
            getSrpFareRequestPayload(
                cardDetails,
                searchData,
                isCfw,
                !!processedData?.isIntl,
                isPeTestVariant,
                !!processedData?.isMultiFare,
                isSpecialRT,
                searchFareRefs
            ),
        [
            cardDetails,
            searchData,
            isCfw,
            processedData?.isIntl,
            isPeTestVariant,
            processedData?.isMultiFare,
            isSpecialRT,
            searchFareRefs
        ]
    )
    const interstitialCacheKey = useMemo(
        () => buildInterstitialCacheKey(interstitialPayload),
        [interstitialPayload]
    )
    const interstitialEntry = useAppSelector(state => selectInterstitialEntry(state, interstitialCacheKey))
    const isInterstitialStale = isInterstitialEntryStale(interstitialEntry)

    /**
     * Single source of truth for what this modal can render. Every fare list,
     * count and special-RT decision below reads from here rather than resolving
     * against the shared `searchData` maps, which the interstitial merge rewrites
     * underneath us.
     */
    const interstitialView = useMemo(
        () =>
            buildInterstitialView({
                cards: cardDetails,
                isRT,
                isSpecialRT,
                searchData,
                entry: interstitialEntry,
                isStale: isInterstitialStale,
                isFetching: interstitialFaresLoading
            }),
        [cardDetails, isRT, isSpecialRT, searchData, interstitialEntry, isInterstitialStale, interstitialFaresLoading]
    )
    const { resolvedSubTravelOptions, resolvedFares } = interstitialView

    useEffect(() => {
        setRumPage('flights_interstitial')
        interstitialStartTimeRef.current = Date.now()
    }, [])

    useEffect(() => {
        if (!interstitialCallComplete || hasFiredSparkloom.current) return
        hasFiredSparkloom.current = true
        trackRumEvent(RUM_EVENT.INTERSTITIAL_LOADED, { latency: Date.now() - interstitialStartTimeRef.current })
    }, [interstitialCallComplete])

    /** Can this modal paint a fare strip right now? Answered by the view, which
     *  resolves fares exactly the way the render does, so the readiness gates and
     *  what is actually on screen can never disagree. */
    const isInterstitialDataReady = interstitialView.status === 'ready'
    const isShowInterstitialFalseInAnyLeg = cardDetails?.some(card => {
        const { subTravelOptionIds = [] } = card || {}
        const { showInterstitial = false } = resolvedSubTravelOptions?.[subTravelOptionIds?.[0]] || {}
        return !showInterstitial
    })

    // The `isSpecialRT` prop is the search response's card-level classification;
    // the view keeps it only if the backend actually returned combined fares to
    // back it up (see buildInterstitialView).
    const effectiveIsSpecialRT = interstitialView.isSpecialRT

    const [onwardFlightDetails, returnFlightDetails] = getCardInfo()
    const { paxCriteria = [] } = getSearchIntentDetails(searchIntent)
    const { headerData: onwardHeaderData, faresObjectList: onwardFares } = onwardFlightDetails || {}
    const { headerData: returnHeaderData, faresObjectList: returnFares } = returnFlightDetails || {}
    const headerDetails: FareFamilyHeaderType[] = [onwardHeaderData, returnHeaderData].filter(Boolean)
    const listOfFares: FareType[][] = [onwardFares, returnFares].filter(Boolean)

    // §9.1 cabin-tab matrix — per-leg cabinClassSummary + BE-driven grouping/pricing.
    // hasCabinMatrix stays false (and every BE-driven computation below short-circuits
    // to the legacy flat list) until a subTravelOption actually carries a
    // cabinClassSummary, so this is a no-op until the interstitial API sends it.
    const cabinMatrixByLeg = useMemo(() => {
        return listOfFares.map((legFares, legIndex) => {
            // Keyed by subTravelOptionId via card.subTravelOptionIds, matching
            // getFaresInfo. Read from the resolved map so the summary survives a
            // special-RT response that carries no per-leg data.
            const subTravelOptionIdsForLeg: string[] = cardDetails[legIndex]?.subTravelOptionIds || []
            const cabinClassSummary = mergeCabinClassSummaries(
                subTravelOptionIdsForLeg.map(stoId => resolvedSubTravelOptions?.[stoId]?.cabinClassSummary)
            )
            const hasCabinMatrix = !!cabinClassSummary && Object.keys(cabinClassSummary).length > 0
            const availableCabins = hasCabinMatrix
                ? getAvailableCabins({ cabinClassSummary, faresArray: legFares })
                : []
            const priceByCabin = hasCabinMatrix ? getMinPriceByCabin(legFares, cabinClassSummary, showPerAdultPricing) : {}
            const fareCountByCabin = hasCabinMatrix ? getCabinFareCount(legFares, cabinClassSummary) : {}
            return { cabinClassSummary, hasCabinMatrix, availableCabins, priceByCabin, fareCountByCabin }
        })
    }, [listOfFares, resolvedSubTravelOptions, cardDetails, showPerAdultPricing])

    const activeLegMatrix = cabinMatrixByLeg[selectedTabIndex] ?? {
        hasCabinMatrix: false,
        availableCabins: [] as CabinTabId[],
        priceByCabin: {},
        fareCountByCabin: {},
        cabinClassSummary: undefined,
    }

    // Special-RT fare strip (combined full-trip fares), resolved by the view: it
    // unions the search-time and interstitial-time fare-combo maps (each can hold
    // a different partial set of cabins) and applies the skeleton floor, so the
    // strip is never blank while a genuine special-RT combo is still loading.
    const specialRtAllFares: FareType[] = effectiveIsSpecialRT ? interstitialView.combinedFares : []
    const specialRtTotalFareCount = interstitialView.combinedFareCount

    // Cabin tabs + per-tab price for special RT, derived from the *combined* special-RT
    // fares that actually exist (client-side grouping by cabinType). Tabs must map 1:1 to
    // bookable combined fares: the special-RT select/create path applies a single combined
    // fareId to BOTH legs (see selectFare / the interstitial create branch), so a cabin with
    // only per-leg fares (no combined SRT fare) is NOT bookable here and must not get a tab —
    // deriving from the per-leg cabinClassSummary would surface empty PE/Business tabs with a
    // placeholder price. This self-heals: when BE returns combined PE/Business SRT fares in
    // specialRTFareMap / travelOptionIdsToFareIdsMap, they flow into specialRtAllFares and the
    // extra tabs appear automatically with no further FE change (mobile parity).
    const specialRtAvailableCabins: CabinTabId[] = effectiveIsSpecialRT
        ? getAvailableCabins({ faresArray: specialRtAllFares })
        : []
    const specialRtPriceByCabin = effectiveIsSpecialRT
        ? getMinPriceByCabin(specialRtAllFares, undefined, showPerAdultPricing)
        : {}

    // The cabin the user originally searched for this leg (e.g. searched "Premium
    // Economy" directly, rather than landing on PE via an upsell) — preferred by
    // getDefaultSelectedCabin when repairing an unavailable selection, and used
    // as the sole tab shown before/without a BE cabinClassSummary.
    const requestedCabinForLeg = searchIntent?.[selectedTabIndex === 0 ? 'J1' : 'J2']?.cabin
    const searchedCabinForLeg: CabinTabId = getDefaultSelectedCabin(requestedCabinForLeg, CABIN_ORDER)

    // Which tabs to actually render: while the interstitial call hasn't resolved
    // a cabinClassSummary yet (still loading, or BE hasn't sent one), show only
    // the single searched-cabin tab. Once resolved, show exactly the cabins BE
    // says are available on this leg — unavailable cabins aren't rendered at all.
    const visibleCabins: CabinTabId[] = effectiveIsSpecialRT
        ? specialRtAvailableCabins.length > 0
            ? specialRtAvailableCabins
            : [searchedCabinForLeg]
        : activeLegMatrix.hasCabinMatrix
          ? activeLegMatrix.availableCabins
          : [searchedCabinForLeg]

    // Render-time guard against a one-tick "empty tab" flash: if BE data shows
    // the stored per-leg selection has no inventory (e.g. just switched to the
    // RT leg that lacks PE), fall back for filtering/highlighting immediately;
    // the useEffect below persists the correction into selectedCabinByLeg. Before
    // any cabinClassSummary exists at all, always show the searched cabin.
    const effectiveSelectedCabin: CabinTabId = effectiveIsSpecialRT
        ? specialRtAvailableCabins.length === 0
            ? searchedCabinForLeg
            : specialRtAvailableCabins.includes(selectedCabin)
              ? selectedCabin
              : specialRtAvailableCabins[0]
        : !activeLegMatrix.hasCabinMatrix
          ? searchedCabinForLeg
          : // Before this leg's one-shot init runs (below), prefer the searched
            // cabin so a PE/Business/First search never flashes ECONOMY on the
            // frame the matrix resolves.
            !cabinInitRef.current[selectedTabIndex]
            ? getDefaultSelectedCabin(requestedCabinForLeg, activeLegMatrix.availableCabins)
            : activeLegMatrix.availableCabins.includes(selectedCabin)
              ? selectedCabin
              : getDefaultSelectedCabin(requestedCabinForLeg, activeLegMatrix.availableCabins)

    // Tabs split on displayCabinType; theme follows fares' cabinType so Indigo
    // Upfront keeps plain Economy chrome (no gold PE header/cards/banner).
    const activeLegFaresForTheme = effectiveIsSpecialRT
        ? specialRtAllFares
        : listOfFares[selectedTabIndex] || []
    const selectedCabinTheme = getCabinThemeForTab(activeLegFaresForTheme, effectiveSelectedCabin)

    // Onward leg's effective *theme* cabin for the RT cross-cabin guard. Up Front
    // and Economy share ECONOMY so they remain interchangeable across legs.
    const onwardLegMatrix = cabinMatrixByLeg[0]
    const onwardRequestedCabin = searchIntent?.J1?.cabin
    const onwardSelectedTab: CabinTabId = !onwardLegMatrix?.hasCabinMatrix
        ? getDefaultSelectedCabin(onwardRequestedCabin, CABIN_ORDER)
        : onwardLegMatrix.availableCabins.includes(selectedCabinByLeg[0])
          ? selectedCabinByLeg[0]
          : getDefaultSelectedCabin(onwardRequestedCabin, onwardLegMatrix.availableCabins)
    const onwardLegCabin =
        getCabinThemeForTab(listOfFares[0] || [], onwardSelectedTab) ||
        (onwardSelectedTab as typeof CABIN_ORDER[number])

    // Skeleton-card count for the active leg. The view already applies the
    // loading floor for responses that arrive without a totalFareCount hint, so
    // the strip shows loaders rather than going blank.
    const activeLegTotalFareCount = interstitialView.legs[selectedTabIndex]?.totalFareCount || 0

    const dispatch = useAppDispatch()
    const createItinerary = useCreateItinerary()
    const { variant: supercoinVariant } = useVariant({ experiment: 'supercoin_toggle', cookieValue: 'supercoinToggle', defaultVariant: 'default_off' }) || ""
    const { wowPricingVariant, wowOn: rtWowOn } = useWowPricingExperiment(!!processedData?.isIntl)
    const wowFaresFromStore = useAppSelector(getWowFares)
    const isDynamicContentLoadingFromStore = useAppSelector(getIsDynamicContentLoading)

    const peTabRef = useRef<HTMLElement | null>(null)
    const [peIntroPhase, setPeIntroPhase] = useState<PeIntroPhase>(PE_INTRO_PHASE.NUDGE)
    const lastIuOfferRef = useRef<ReturnType<typeof resolvePeUpgradeOffer> | null>(null)
    const [showCabinMediaDrawer, setShowCabinMediaDrawer] = useState(false)

    // Price-integrity / flight-unavailable dialog surfaced from the
    // fetch-interstitial-page `userDialogue` (PRICE_INCREASE | PRICE_DECREASE |
    // FLIGHT_NOT_AVAILABLE), mirroring flights-pwa. PE-experiment gated.
    const [interstitialAlert, setInterstitialAlert] = useState<{
        type: InterstitialAlertType | null
        isOpen: boolean
        variant?: PriceChangeVariant
        title?: string
        subTitle?: string
        newPrice?: string
        priceDelta?: string
    }>({ type: null, isOpen: false })

    const showPeInterstitialLayout = !loading && isPeTestVariant
    // PE promo tile is limited to the Economy search flow (searched cabin, trip-level).
    const isEconomySearchFlow = !isSrpThemeCabin(searchIntent?.J1?.cabin)
    const galleryResources = useMemo(
        () => getPeGalleryResourcesForCard(cardDetails[selectedTabIndex], resolvedSubTravelOptions),
        [cardDetails, selectedTabIndex, resolvedSubTravelOptions]
    )
    // Image-only URLs (videos stripped) for the PE experience carousel — the full
    // gallery (incl. videos) still feeds the CabinMediaDrawer story viewer.
    const peCarouselImages = useMemo(
        () =>
            galleryResources
                .map(resource => resource.resourceUrl)
                .filter(url => !!url && !isVideoMediaUrl(url)),
        [galleryResources]
    )
   
    // "Data for premium economy is there": the interstitial call has resolved
    // AND BE actually confirms PE inventory on this leg (non-null minCabinPrice
    // + non-empty cabinFareIds, via getAvailableCabins). hasCabinMatrix alone
    // already implies "call resolved" — it only ever becomes true after the
    // fetch merges a cabinClassSummary into Redux.
    const isPeAvailableForLeg = activeLegMatrix.hasCabinMatrix && activeLegMatrix.availableCabins.includes('PREMIUM_ECONOMY')
    const isIuAvailableForLeg = activeLegMatrix.hasCabinMatrix && activeLegMatrix.availableCabins.includes(INDIGO_UPFRONT_CABIN as CabinTabId)
    // Real BE-driven Economy upgrade offer (same 7439 resolver). IU chrome
    // follows upgradeCallout.targetCabin only — never inventory presence.
    const peUpgradeOffer = useMemo(() => {
        if (!showPeInterstitialLayout) return null
        const activeLegFares = effectiveIsSpecialRT ? specialRtAllFares : listOfFares[selectedTabIndex]
        const economyFares = activeLegMatrix.hasCabinMatrix
            ? filterFaresByCabin(activeLegFares, 'ECONOMY', activeLegMatrix.cabinClassSummary)
            : activeLegFares
       
        // Always resolve from Economy fares — Upgrade selects IU, so the selected
        // fare is no longer Economy when the user returns to this tab.
        const economyFare =
            findFareWithUpgradeCallout(economyFares) ||
            findFareWithUpgradeCallout(activeLegFares) ||
            economyFares?.[0]
        return resolvePeUpgradeOffer({ economyFare, cabinSummary: activeLegMatrix.cabinClassSummary })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPeInterstitialLayout, selectedTabIndex, listOfFares, specialRtAllFares, activeLegMatrix.hasCabinMatrix, activeLegMatrix.cabinClassSummary])
    const isIndigoUpsell = isIndigoUpfrontCabin(peUpgradeOffer?.targetCabin)
    if (isIndigoUpsell && peUpgradeOffer?.eligible) {
        lastIuOfferRef.current = peUpgradeOffer
    }
    const iuOffer = isIndigoUpsell && peUpgradeOffer?.eligible ? peUpgradeOffer : lastIuOfferRef.current
    useEffect(() => {
        if (!isIndigoUpsell || !peUpgradeOffer?.eligible || !peUpgradeOffer.title) return
        persistIuItineraryCallout({
            targetCabin: peUpgradeOffer.targetCabin || INDIGO_UPFRONT_CABIN,
            amount: peUpgradeOffer.amount,
            title: peUpgradeOffer.title,
            baseResource: peUpgradeOffer.videoUrl || peUpgradeOffer.baseResource || undefined,
            resources: peUpgradeOffer.mediaResources,
        })
    }, [isIndigoUpsell, peUpgradeOffer])
    // BE has no caption field — IU uses Figma copy, same as PE uses copy.header.
    const caption = (isIndigoUpsell || isIuAvailableForLeg)
        ? INDIGO_CAPTION_CONFIG.copy.caption
        : PE_NUDGE_BANNER_COPY_NUDGE
    const upgradeNudgeVariant =
        isIndigoUpsell || isIndigoUpfrontCabin(iuOffer?.targetCabin) || isIuAvailableForLeg
            ? UPGRADE_NUDGE_VARIANT.INDIGO
            : UPGRADE_NUDGE_VARIANT.PREMIUM
    const captionCaretTabId = (isIndigoUpsell || isIuAvailableForLeg) ? INDIGO_UPFRONT_CABIN : 'PREMIUM_ECONOMY'
    const hasIuColumnCard = !!(iuOffer?.title)
    const isIuActive = isIndigoUpsell || hasIuColumnCard || isIuAvailableForLeg
    // PE suppressed on all round-trip flows; IU is allowed on RT.
    // Caption/upsell only on the Economy tab — never on Indigo Upfront.
    const isEconomyCabinTab =
        showPeInterstitialLayout &&
        (!isRT || isIuActive) &&
        !effectiveIsSpecialRT &&
        effectiveSelectedCabin === 'ECONOMY'
    const showPeIntro =
        isEconomyCabinTab &&
        (isPeAvailableForLeg || isIuActive)
    // Same phase gates for PE and IU — only the view-tracking object differs.
    // 31/month cap (one count per interstitial open) + per-search interaction suppression.
    const nudgeKind = isIndigoUpsell ? NUDGE_KIND.INDIGO : NUDGE_KIND.PE
    const nudgeQuery = useMemo(() => JSON.stringify(searchIntent ?? null), [searchIntent])
    const nudgeGateOpen = usePeNudgeGate({
        eligible: showPeIntro && !isNudgeSuppressed(nudgeKind, nudgeQuery),
        kind: nudgeKind,
        query: nudgeQuery,
    })
    const showPeNudgeBanner =
        showPeIntro &&
        nudgeGateOpen &&
        (peIntroPhase === PE_INTRO_PHASE.NUDGE || peIntroPhase === PE_INTRO_PHASE.EXITING)
    // Callout mounts during EXITING so its enter animation runs in parallel
    // with the nudge fade-out (same PE_INTRO_TRANSITION_MS duration).
    const showPeUpgradeCallout =
        showPeIntro &&
        nudgeGateOpen &&
        (peIntroPhase === PE_INTRO_PHASE.EXITING || peIntroPhase === PE_INTRO_PHASE.UPGRADE)
    const isPeNudgeBannerExiting = peIntroPhase === PE_INTRO_PHASE.EXITING

    // Visiting the upgrade target tab suppresses that nudge for this search, this session.
    useEffect(() => {
        if (!showPeInterstitialLayout) return
        if (effectiveSelectedCabin === 'PREMIUM_ECONOMY' || isIndigoUpfrontCabin(effectiveSelectedCabin)) {
            onPremiumFlowInteraction(effectiveSelectedCabin, nudgeQuery)
            setPeIntroPhase(PE_INTRO_PHASE.SUPPRESSED)
        }
    }, [effectiveSelectedCabin, showPeInterstitialLayout, nudgeQuery])

    // §9.1 — repair the per-leg cabin selection if BE data reveals the
    // currently selected cabin has no inventory on this leg (e.g. RT return
    // leg lacks PE availability) — falls back to ECONOMY/first-available so
    // the tab row and the filtered fare list never disagree. No-ops until a
    // cabinClassSummary is actually present (dark launch / BE not live yet).
    useEffect(() => {
        if (!showPeInterstitialLayout || effectiveIsSpecialRT) return
        if (!activeLegMatrix.hasCabinMatrix) return
       
        if (!cabinInitRef.current[selectedTabIndex]) {
            cabinInitRef.current[selectedTabIndex] = true
            const initialCabin = getDefaultSelectedCabin(requestedCabinForLeg, activeLegMatrix.availableCabins)
            if (initialCabin !== selectedCabin) handleCabinSelect(initialCabin)
            return
        }
        if (activeLegMatrix.availableCabins.includes(selectedCabin)) return
        handleCabinSelect(getDefaultSelectedCabin(requestedCabinForLeg, activeLegMatrix.availableCabins))
        // `showPeInterstitialLayout` MUST stay in the deps: on the reuse (reopen) path the
        // merged data makes `hasCabinMatrix` true at mount, but the PE experiment hook's
        // `loading` resets on remount so `showPeInterstitialLayout` is briefly false. Without
        // this dep the effect never re-runs when it flips true, `cabinInitRef` stays unset, and
        // `effectiveSelectedCabin` is frozen at ECONOMY — clicking Business/PE does nothing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTabIndex, activeLegMatrix.hasCabinMatrix, activeLegMatrix.availableCabins.join(','), showPeInterstitialLayout])

    // Which cabins the user could see on this leg of the fare modal.
    useEffect(() => {
        const isDataReady =
            showPeInterstitialLayout || isCfw ? interstitialCallComplete : isInterstitialDataReady
        if (!isDataReady || !visibleCabins.length || classImpressionLegRef.current === selectedTabIndex) return
        classImpressionLegRef.current = selectedTabIndex
        const cabinLabels = visibleCabins.map(cabin => getCabinTabLabel(cabin) || cabin).join(',')
        ravenSDKSrpCfwTrigger('a_impression', searchData, processedData, {
            action_name: 'a_class_impression',
            action_type: 'impression',
            page_name: 'flights_srp',
            a_class_choose: cabinLabels
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        interstitialCallComplete,
        isInterstitialDataReady,
        showPeInterstitialLayout,
        isCfw,
        selectedTabIndex,
        visibleCabins.join(',')
    ])

    useEffect(() => {
        if (!showPeIntro || !nudgeGateOpen) return

        setPeIntroPhase(PE_INTRO_PHASE.NUDGE)
        const exitTimer = setTimeout(() => setPeIntroPhase(PE_INTRO_PHASE.EXITING), PE_INTRO_NUDGE_DURATION_MS)
        const upgradeTimer = setTimeout(() => {
            setPeIntroPhase(PE_INTRO_PHASE.UPGRADE)
        }, PE_INTRO_NUDGE_DURATION_MS + PE_INTRO_TRANSITION_MS)

        return () => {
            clearTimeout(exitTimer)
            clearTimeout(upgradeTimer)
        }
    }, [showPeIntro, nudgeGateOpen, selectedTabIndex, isIndigoUpsell, caption])

    const handlePeTabRef = (el: HTMLElement | null) => {
        peTabRef.current = el
    }

    /**
     * Issues the interstitial request via the cache thunk. Returns `null` when
     * RTK aborted a duplicate in-flight call (StrictMode / fast remount) so the
     * caller skips merge / alert side effects — the winning call owns those.
     * On a real failure returns `{}` so existing callers keep their shape.
     */
    const fetchInterstitialPageData = async (): Promise<Partial<InterstitialData> | null> => {
        const result = await dispatch(
            fetchInterstitialData({
                key: interstitialCacheKey,
                dataId: dataId || '',
                payload: interstitialPayload,
                isCfw
            })
        )
        if (fetchInterstitialData.rejected.match(result) && result.meta.condition) {
            return null
        }
        if (fetchInterstitialData.fulfilled.match(result)) {
            return (result.payload as Partial<InterstitialData>) || {}
        }
        return {}
    }

    /**
     * WOW pricing fetch on interstitial — fires right after the interstitial
     * data lands so the fare-family rows can show the WOW callout/strikethrough.
     * Mirrors flights-pwa InterstitialPage's `fetchWowPricingSrpData` (UIENG-6192).
     */
    const fetchInterstitialWowPricing = async (freshData?: Partial<InterstitialData>) => {
        // Declared outside `try` so the `finally` can clear the loading ids.
        let interstitialFareIds: string[] = []
        try {
            if (!isUserSignedIn()) return
            if (!rtWowOn) return
            // Key by the actual cards, not the optional `cardKey` prop — the
            // oneway path (TuppleRenderer) doesn't pass cardKey, so keying on it
            // collapses every oneway interstitial to `${dataId}|`, blocking the
            // WOW fetch for all interstitials after the first (UIENG bug).
            const interstitialDedupKey = `${dataId || ''}|${cardDetails
                .map(c => c?.travelOptionId)
                .join(',')}`
            if (interstitialWowFired.has(interstitialDedupKey)) return
            interstitialWowFired.add(interstitialDedupKey)
            // Mark EVERY fare in the modal as loading (not just the global flag),
            // so each FareCard's WowFareCallout shimmers while the fetch is in
            // flight — `WowFareCallout` keys its shimmer on `wowLoadingFareIds`
            // per fareId, exactly like the SRP tupples. The global flag alone
            // only ends up covering the selected (first) fare.
            //
            // IMPORTANT: in the oneway flow this runs right after
            // `mergeInterstitialData`, so the closure's `fares`/`subTravelOptions`
            // are still PRE-merge and only know the lead fare. Use the freshly
            // fetched interstitial data (merged over the closure) so we collect
            // ALL fare-family fareIds, not just the first.
            const stoSource = { ...(subTravelOptions || {}), ...(freshData?.subTravelOptions || {}) }
            const faresSource = { ...(fares || {}), ...(freshData?.fares || {}) }
            interstitialFareIds = (cardDetails || [])
                .flatMap(card => getFaresInfo(card, stoSource, faresSource) || [])
                .map((fare: any) => fare?.fareId)
                .filter(Boolean)
            dispatch(setDynamicContentLoading(true))
            if (interstitialFareIds.length) {
                dispatch(addWowLoadingFareIds(interstitialFareIds))
            }
            const selectedCards = convertSelectedFlightsToSelectedCards(cardDetails)
            const payload = getFetchContentPayload(
                searchData,
                {
                    isIntl: !!processedData?.isIntl,
                    tripType: isRT ? 'roundtrip' : 'oneway',
                    isCfw: !!processedData?.isCfw,
                    isInterstitial: true,
                    wowPricingVariant
                },
                selectedCards
            )
            const wowResponse = (await getWowPricingSrpData(
                payload,
                !!processedData?.isCfw
            )) as any
            const responseBody = wowResponse?.data ?? wowResponse ?? {}
            if (responseBody?.fares && !isEmpty(responseBody.fares)) {
                dispatch(mergeWowFares(responseBody.fares))
            }
        } catch (e) {
            console.error('Interstitial WOW pricing fetch failed:', e)
        } finally {
            dispatch(setDynamicContentLoading(false))
            if (interstitialFareIds.length) {
                dispatch(removeWowLoadingFareIds(interstitialFareIds))
            }
        }
    }

    const hasFetchedInterstitialRef = useRef(false)
    useEffect(() => {
        if (hasFetchedInterstitialRef.current) return

        // Reuse: the cache already holds a complete response for exactly this
        // request. Unlike the previous flag-based gate this cannot outlive the
        // data it describes — the view resolves fares the same way the render
        // does, so "reusable" and "renderable" are the same question.
        if (!interstitialView.needsFetch) {
            hasFetchedInterstitialRef.current = true
            setInterstitialCallComplete(true)
            setInterstitialFaresLoading(false)
            // Fares are cached, but the WOW callouts still need their fetch.
            fetchInterstitialWowPricing()
            return
        }

        // Statsig/PE forces a multi-cabin fetch. Read synchronously via
        // `isPeDiscoveryRequestEnabled()` rather than the loading-gated
        // `showPeInterstitialLayout`, which stays true if Statsig fails to
        // initialise and would block the fetch outright.
        const shouldFetch =
            isCfw || isShowInterstitialFalseInAnyLeg || interstitialView.needsFetch || isPeTestVariant
        if (!shouldFetch) {
            fetchInterstitialWowPricing()
            return
        }
        hasFetchedInterstitialRef.current = true
        // A stale entry keeps painting while it refreshes behind the strip;
        // only show the loading state when there is genuinely nothing to show.
        setInterstitialFaresLoading(interstitialView.status !== 'ready')
        fetchInterstitialPageData()
            .then(res => {
                    // Duplicate in-flight call was aborted — the winning request owns merge / alerts.
                    if (res === null) return
                    // Regular RT/OW ship top-level fares+subTravelOptions; special-RT ships its
                    // fare-combo mapping (travelOptionIdsToFareIdsMap) instead. Either constitutes a
                    // successful interstitial payload worth merging + flagging for reuse.
                    const hasMergeableInterstitialData =
                        !!(res.fares && res.subTravelOptions) || !isEmpty((res as any)?.travelOptionIdsToFareIdsMap)
                    if (isPeTestVariant) {
                    const alert = mapUserDialogueToInterstitialAlert(
                        extractInterstitialUserDialogue(res) || extractInterstitialUserDialogue((res as any)?.data)
                    )
                    if (alert) {
                        // A price change also ships the re-priced fares — merge them so the
                        // strip behind the dialog shows the new price. FNA ships no fares.
                        if (hasMergeableInterstitialData) {
                            dispatch(mergeInterstitialData(res as InterstitialData))
                            if (!isEmpty(res?.couponUIResponse?.coupons)) {
                                dispatch(setCouponsData(res.couponUIResponse.coupons))
                            }
                        }
                        const isFna = alert.type === INTERSTITIAL_ALERT_TYPE.FLIGHT_UNAVAILABLE
                        if (isFna) {
                            trackRumEvent(RUM_EVENT.FLIGHT_NOT_AVAILABLE, { fnaSource: 'interstitial' })
                        }
                        setInterstitialAlert({
                            type: alert.type,
                            isOpen: true,
                            variant: alert.variant,
                            // Keep the Figma copy for price changes (title/subTitle drive
                            // only the FNA dialog); price changes rely on newPrice/priceDelta.
                            title: isFna ? alert.title : undefined,
                            subTitle: isFna ? alert.subTitle : undefined,
                            newPrice: alert.newPrice,
                            priceDelta: alert.priceDelta
                        })
                        return
                    }
                }
                if (hasMergeableInterstitialData) {
                    dispatch(
                        mergeInterstitialData(res as InterstitialData)
                    )
                    if (!isEmpty(res?.couponUIResponse?.coupons)) {
                        dispatch(setCouponsData(res.couponUIResponse.coupons))
                    }
                    // Now that interstitial data is in Redux, fire WOW. Pass `res` so the
                    // loading-fareId collection uses the freshly fetched fares (the closure
                    // is still pre-merge).
                    fetchInterstitialWowPricing(res as InterstitialData)
                } else {
                    // api fail / dialogue-only response — but if we already have interstitial fares
                    // to render (e.g. a forced-PE re-fetch on an already-populated RT: the second
                    // fetch-interstitial-page call sends the merged fareIds and BE replies without a
                    // top-level subTravelOptions), keep the modal open and show what we have instead
                    // of silently bailing to createItinerary. Without this, re-opening the modal on a
                    // PE round-trip skips the interstitial entirely and jumps straight to review.
                    const hasRenderableFares =
                        isInterstitialDataReady ||
                        (!isEmpty(getFaresInfo(cardDetails[0], resolvedSubTravelOptions, resolvedFares)) &&
                            (!isRT || !isEmpty(getFaresInfo(cardDetails[1], resolvedSubTravelOptions, resolvedFares))))
                    if (hasRenderableFares) return
                    const baggageEnabledAtTripLevel = false //showCheckInBaggagePriceOW && showCheckInBaggagePriceRT
                    const [onwardFlight, returnFlight] = cardDetails || []
                    const { fareList: specialRtFareList = [] } = getSpecialRTFareCase(onwardFlight, returnFlight, fares, baggageEnabledAtTripLevel, specialRTFareMap)
                    let selectedFares: any[] = []
                    if (isSpecialRT) {
                        const onwardSpecialRtFares = getSpecialRtFaresInfo(specialRtFareList, onwardFlight, resolvedSubTravelOptions, resolvedFares)
                        const returnSpecialRtFares = getSpecialRtFaresInfo(specialRtFareList, returnFlight, resolvedSubTravelOptions, resolvedFares)
                        selectedFares = [onwardSpecialRtFares?.[0] || {}, returnSpecialRtFares?.[0] || {}]
                    } else {
                        selectedFares = [
                            getFaresInfo(cardDetails[0], resolvedSubTravelOptions, resolvedFares)[0],
                            getFaresInfo(cardDetails[1], resolvedSubTravelOptions, resolvedFares)[0]
                        ]
                    }
                    createItinerary({ cardDetails, selectedFares, tuppleIndex, supercoinVariant, isFFFlow, isSpecialRT, type: 'interstitial' })
                    closeModal && closeModal()
                }
            })
            .finally(() => {
                setInterstitialCallComplete(true)
                setInterstitialFaresLoading(false)
                dispatch(setServicesLoading(true))
            })
    }, [interstitialCacheKey, interstitialView.needsFetch])
    useEffect(()=>{
        // PE always re-fetches interstitial; wait for that call so cabin-filtered fares exist.
        const isDataReady = showPeInterstitialLayout
            ? interstitialCallComplete
            : isCfw
              ? interstitialCallComplete
              : isInterstitialDataReady
        if (!isDataReady || !isEmpty(selectedFares[selectedTabIndex])) return

        // Special RT: selectFare stamps the chosen fareId onto BOTH legs. Auto-select
        // must use the combined SRT fare strip — getFaresInfo returns per-leg ONEWAY
        // fares, and duplicating those onto J1/J2 causes fare-validation FnAs.
        if (effectiveIsSpecialRT) {
            const srtFares =
                showPeInterstitialLayout && specialRtAvailableCabins.length > 0
                    ? filterFaresByCabin(specialRtAllFares, effectiveSelectedCabin)
                    : specialRtAllFares
            const cheapestFare = srtFares.find(fare => fare.isCheapest) || srtFares[0]
            if (isEmpty(cheapestFare)) return
            const cheapestIndex = srtFares.findIndex(fare => fare.fareId === cheapestFare.fareId)
            selectFare(cheapestFare as FareType, cheapestIndex >= 0 ? cheapestIndex : 0)
            return
        }

        const legFares = listOfFares[selectedTabIndex] || []
        if (showPeInterstitialLayout && activeLegMatrix.hasCabinMatrix) {
            // Only auto-select for the cabin the user searched for (initial open).
            // PE / Business / First tab switches leave cards unselected — the upgrade
            // CTA (handlePeUpgrade) is the only path that pre-selects a non-search cabin.
            //
            // Match flights-pwa InterstitialPage: pick BE `cheapestFareId` (`isCheapest`)
            // inside the searched cabin, not min display price. Display-price min can
            // diverge from cheapestFareId (slash / WOW / per-adult), which made first
            // open and cached reopen land on different fares.
            if (effectiveSelectedCabin !== searchedCabinForLeg) return

            const cabinFares = filterFaresByCabin(
                legFares,
                effectiveSelectedCabin,
                activeLegMatrix.cabinClassSummary
            )
            const cheapestFareId = (cardDetails[selectedTabIndex]?.subTravelOptionIds || [])
                .map((stoId: string) => subTravelOptions?.[stoId]?.cheapestFareId)
                .find(Boolean)
            const cheapestFare =
                cabinFares.find(fare => fare.fareId === cheapestFareId) ||
                cabinFares.find(fare => fare.isCheapest) ||
                cabinFares[0]
            if (isEmpty(cheapestFare)) return
            const cheapestIndex = cabinFares.findIndex(fare => fare.fareId === cheapestFare.fareId)
            selectFare(cheapestFare as FareType, cheapestIndex >= 0 ? cheapestIndex : 0)
            return
        }

        const defaultFare = getFaresInfo(cardDetails[selectedTabIndex], subTravelOptions, fares)[0]
        if (!isEmpty(defaultFare)) {
            selectFare(defaultFare, 0)
        }
    }, [interstitialCallComplete, isInterstitialDataReady, selectedTabIndex, showPeInterstitialLayout, effectiveSelectedCabin, activeLegMatrix.hasCabinMatrix, searchedCabinForLeg, effectiveIsSpecialRT, specialRtAllFares, specialRtAvailableCabins.length])

    /**
     * RT single-cabin sync (CTAIR-2598) — ported from flights-pwa's InterstitialPage.
     *
     * Cross-cabin rules use *theme* cabin (cabinType): Up Front ↔ Economy are allowed.
     * PE / Business / First stay locked vs Economy / Up Front.
     */
    useEffect(() => {
        if (!isRT || effectiveIsSpecialRT || !showPeInterstitialLayout) return
        if (!interstitialCallComplete || !cardDetails?.[1]) return
        const onwardCabin = getFareCabin(selectedFares?.[0])
        if (!onwardCabin) return
        const returnCabin = getFareCabin(selectedFares?.[1])
        if (returnCabin === onwardCabin) return
        const returnFares = listOfFares[1] || []
        const matchingFare = getCheapestFareForThemeCabin(
            returnFares,
            onwardCabin,
            showPerAdultPricing
        )
        if (!matchingFare) return
        const matchingIndex = returnFares.findIndex(fare => fare.fareId === matchingFare.fareId)
        selectFare(matchingFare as FareType, matchingIndex >= 0 ? matchingIndex : 0, true)
        // Land the return tab on a display cabin that shares the onward theme —
        // prefer the exact display cabin of the matched fare, else ECONOMY for theme ECONOMY.
        const returnDisplayCabin = matchingFare
            ? (matchingFare as FareType & { displayCabinType?: string }).displayCabinType || onwardCabin
            : onwardCabin
        cabinInitRef.current[1] = true
        handleCabinSelect(
            (String(returnDisplayCabin).toUpperCase().replace(/[\s-]+/g, '_') || onwardCabin) as CabinTabId,
            1
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFares, isRT, effectiveIsSpecialRT, showPeInterstitialLayout, interstitialCallComplete])

    useEffect(()=>{
        // Special RT has a single combined fare on both slots — per-leg branded sync
        // would pull an ONEWAY fare from listOfFares[1] and stamp it onto both legs.
        if (!isRT || effectiveIsSpecialRT) return
        const isOnwardBranded = isCTBrandedFareCardSelectedOnward(selectedFares)
        const isReturnBranded = isCTBrandedFareCardSelectedReturn(selectedFares)
        if ((isOnwardBranded && !isReturnBranded) || (!isOnwardBranded && isReturnBranded)) {
            const returnFareArray = listOfFares[1]
            const reArrangedFareArray = arrangeFaresArray(returnFareArray, selectedFares, 1)
            selectFare(reArrangedFareArray[0],0, true)
        }
    }, [selectedFares, effectiveIsSpecialRT])
    const ffCoverageRef = useRef(false)

    const selectFare = (fare: FareType, index: number, isSelectReturnLegFare: boolean = false) => {
        // Selecting the upgrade-target fare suppresses that nudge for this search.
        const nudgeFareCabin = resolveThemeCabin(fare) === 'PREMIUM_ECONOMY'
            ? 'PREMIUM_ECONOMY'
            : fare?.displayCabinType || fare?.displayCabinClass
        if (nudgeFareCabin) {
            onPremiumFlowInteraction(nudgeFareCabin, nudgeQuery)
            if (resolveThemeCabin(fare) === 'PREMIUM_ECONOMY' || isIndigoUpfrontCabin(nudgeFareCabin)) {
                setPeIntroPhase(PE_INTRO_PHASE.SUPPRESSED)
            }
        }
        const selectedLeg = isSelectReturnLegFare ? 1 : selectedTabIndex
        const benifitsProvided=  getBenefitProvided(fare, servicesData, baggageAllowances)
        let updatedSelection = [...selectedFares]
        const cards = [cardDetails[0], cardDetails[1]]
        if (effectiveIsSpecialRT) {
            const specialRtFareOw = getSpecialRtFaresInfo([fare?.fareId], cards[0], resolvedSubTravelOptions, resolvedFares, false)
            const specialRtFareRt = getSpecialRtFaresInfo([fare.fareId], cards[1], resolvedSubTravelOptions, resolvedFares, false)
            updatedSelection = [specialRtFareOw?.[0] || {}, specialRtFareRt?.[0] || {}]
        } else {
            updatedSelection[selectedLeg] = fare
        }
        const updatedFareIndex = [...selectedFareIndex]
        updatedFareIndex[selectedLeg] = index
        setSelectedFareIndex(updatedFareIndex)
        setSelectedFares(updatedSelection)

        const flightNumberValue: string[] = []
        const airlineNamesValue: string[] = []
        cardDetails?.map(card => {
            const { summary } = card || {}
            const { flights = [] } = summary || {}
            flights.map((flight: any) => {
                flightNumberValue.push(`${flight?.airlineCode}-${flight?.flightNumber}`)
                airlineNamesValue.push(metaData?.airlineDetail?.[flight?.airlineCode]?.name)
            })
        })
        // WoW pricing attributes for the selected fare (mirrors flights-pwa flex-fare select).
        const wowPricingObj = showPerAdultPricing
            ? wowFaresFromStore?.[fare?.fareId]?.pricing?.perAdultPricing
            : wowFaresFromStore?.[fare?.fareId]?.pricing?.totalPricing
        const { couponDetails: wowCoupon } = wowPricingObj || {}
        const ravenPayload = {
            action_name: !isFFFlow ? `select_cfw_fare_${selectedLeg === 0 ? 'ow' : 'rt'}_click`: `select_flex_fare_${selectedLeg === 0 ? 'onward' : 'return'}_journey_click`,
            action_type: 'click',
            index: selectedFareIndex?.[selectedLeg] + 1 || 1,
            available_fares: listOfFares?.[selectedLeg]?.length || 0,
            a_fare_type: fare?.displayText?.displayTitle || 'N/A',
            a_fare_price: fare?.pricing?.totalPricing?.totalPrice || 0,
            page_name: 'flights_srp',
            a_flight_tuple_num: tuppleIndex !== undefined ? tuppleIndex + 1 : 'N/A',
            a_flight_number: flightNumberValue?.join(',') || 'N/A',
            a_airline: airlineNamesValue?.join(',') || 'N/A',
            a_ff_fare_type: fare?.displayText?.displayTitle || 'N/A',
            event_description1: Object.values(benifitsProvided).filter(Boolean).join(' | ') || 'N/A',
            a_srp_ff_cookie: getFFCookieValue(),
            a_wow_amount: wowCoupon?.wowDisplayPrice ?? 'N/A',
            a_wow_callout: wowCoupon?.message || 'N/A',
            ct_coupon_applied: wowCoupon?.couponCode || 'N/A'
        }
        // Cabin of the fare the user actually picked, which diverges from the searched
        // cabin once they select from the PE/Business tab.
        const selectedFareCabin = getFareCabin(fare)
        ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, {
            ...ravenPayload,
            a_class_choose: (selectedFareCabin && cabinClassToLabel[selectedFareCabin]) || 'N/A'
        })
    }

    const getAvailableBenefitsInSrpResponse = () => {
        try {
            const payloadForStore: any = {}
            cardDetails.forEach(card => {
                const { travelOptionId } = card
                if (!isEmpty(cardIdToBenefitsMap[travelOptionId])) {
                    return
                }
                const faresArray: FareType[] = getFaresInfo(card, subTravelOptions, fares)

                payloadForStore[travelOptionId] = {
                    travelOptionId,
                    subTravelOptions: []
                }

                faresArray.forEach(fare => {
                    const { fareId, subTravelOptionId, subTravelOptionKey } = fare || {}
                    const subTravelOption = payloadForStore[travelOptionId].subTravelOptions.find(
                        (option: any) => option.subTravelOptionId === subTravelOptionId
                    )

                    const { benefits } = fare || {}
                    const { baggageAllowanceId = '', benefitIds = [], penalty = [] } = benefits || {}

                    if (isEmpty(subTravelOption)) {
                        payloadForStore[travelOptionId].subTravelOptions.push({
                            subTravelOptionId,
                            subTravelOptionKey,
                            benefits: {
                                [fareId]: {
                                    baggageAllowanceId,
                                    benefitIds,
                                    penalty
                                }
                            }
                        })
                    } else {
                        subTravelOption.benefits = {
                            ...subTravelOption.benefits,
                            [fareId]: {
                                baggageAllowanceId,
                                benefitIds,
                                penalty
                            }
                        }
                    }
                })
            })
            return payloadForStore
        } catch (ex) {
            return {}
        }
    }

    const fetchMissingBenefitsInCards = ({ isRetry }: { isRetry: boolean }) => {
        const payload: { dataId: string; travelOptions: Record<string, any> } = {
            dataId,
            travelOptions: {}
        }
        const paxInfos = paxCriteria.map((paxDetails: { type: any; count: any }) => ({
            paxType: paxDetails.type,
            paxCount: paxDetails.count
        }))

        cardDetails.forEach((card, index) => {
            const { travelOptionId, summary } = card
            const { firstDeparture: { airport: { time = '', zoneId = '' } = {} } = {} } = summary || {}
            const departureTime = {
                time,
                zoneId
            }
            const journeyKey = travelOptionId
            if (!isRetry && !isEmpty(cardIdToBenefitsMap[travelOptionId])) {
                return
            }
            const missingServicesData: any = {
                travelOptionId,
                subTravelOptions: [],
                paxInfos
            }
            const faresArray: FareType[] = getFaresInfo(card, subTravelOptions, fares)
            faresArray.forEach(fare => {
                const missingServices: any = []
                const { fareId, subTravelOptionId: fareSubTravelOptionId, pricing, subTravelOptionKey } = fare || {}
                if (isRetry) {
                    const { subTravelOptions = [] } = cardIdToBenefitsMap[travelOptionId] || {}
                    subTravelOptions.forEach((subTravelOption: any) => {
                        if (subTravelOption?.subTravelOptionId === fareSubTravelOptionId) {
                            const { benefits = {} } = subTravelOption || {}
                            const { baggageAllowances = {}, benefitIds = [], penalties = {} } = benefits[fareId] || {}
                            if (isEmpty(baggageAllowances)) {
                                missingServices.push('BAGGAGE')
                            }
                            if (isEmpty(benefitIds) || benefitIds.length < 2) {
                                missingServices.push('FARE_BENEFITS')
                            }
                            if (isEmpty(penalties) || penalties.length < 2) {
                                missingServices.push('PENALTIES')
                            }
                        }
                    })
                } else {
                    const { benefits } = fare || {}
                    const { baggageAllowanceId = '', benefitIds = [], penalty = [] } = benefits || {}
                    if (isEmpty(baggageAllowanceId)) {
                        missingServices.push('BAGGAGE')
                    }
                    if (isEmpty(benefitIds) || !Array.isArray(benefitIds) || benefitIds.length < 2) {
                        missingServices.push('FARE_BENEFITS')
                    }
                    if (isEmpty(penalty) || !Array.isArray(penalty) || penalty.length < 2) {
                        missingServices.push('PENALTIES')
                    }
                }
                if (missingServices.length > 0) {
                    const indexOfFareSubtravelOption = missingServicesData.subTravelOptions.findIndex(
                        ({ subTravelOptionId }: any) => fareSubTravelOptionId === subTravelOptionId
                    )
                    const missingServicesObj = {
                        fareId,
                        perPaxTotalPrice: pricing?.perAdultPricing?.totalPrice,
                        requiredBenefitTypes: missingServices
                    }
                    if (indexOfFareSubtravelOption === -1) {
                        missingServicesData.subTravelOptions.push({
                            subTravelOptionId: fareSubTravelOptionId,
                            fares: [missingServicesObj],
                            isInternational: !!processedData?.isIntl,
                            subTravelOptionKey,
                            departureTime
                        })
                    } else {
                        missingServicesData.subTravelOptions[indexOfFareSubtravelOption]?.fares?.push(
                            missingServicesObj
                        )
                    }
                }
            })
            if (missingServicesData.subTravelOptions.length > 0) {
                payload.travelOptions = {
                    ...payload.travelOptions,
                    [journeyKey]: missingServicesData
                }
            }
        })
        if (!isEmpty(payload.travelOptions)) {
            dispatch(fetchServicesData(payload))
        } else {
            dispatch(setServicesLoading(false))
        }
    }

    const onRetryForBenefits = (id:any) => {
        const selectedFare =  listOfFares?.[0]?.find((fare:any)=>fare?.fareId === id)
        setRetryCountForServices(prev => prev + 1)
        const ravenPayload = {
            action_name: 'reload_ff_benefits_click',
            action_type: 'click',
            index: selectedFareIndex?.[selectedTabIndex] + 1 || 1,
            available_fares: listOfFares?.[selectedTabIndex]?.length || 0,
            page_name: 'flights_srp',
            tuple_num:tuppleIndex !== null ? tuppleIndex + 1 : 'N/A',
            a_srp_ff_cookie: getFFCookieValue(),
            a_ff_fare_type: selectedFare?.displayText?.displaySubTitle || 'N/A',
            a_fare_price: selectedFare?.pricing?.totalPricing?.totalPrice || 0
        }
        ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, { ...ravenPayload })
    }

    useEffect(() => {
        if (interstitialCallComplete || (!isCfw && isInterstitialDataReady)) {
            const availableBenefits = getAvailableBenefitsInSrpResponse()
            if (!isEmpty(availableBenefits)) {
                dispatch(setCardIdToBenefitsMap(availableBenefits))
            }
            fetchMissingBenefitsInCards({ isRetry: false })
        }
    }, [interstitialCallComplete, isInterstitialDataReady])

    useEffect(() => {
        if (retryCountForServices > 0 && retryCountForServices < 3) {
            fetchMissingBenefitsInCards({ isRetry: true })
        }
    }, [retryCountForServices])

    const getTooltipContent = () => {
        // Special RT holds the same whole-trip fare in both slots, so summing both would double every row.
        const faresForBreakup = effectiveIsSpecialRT ? selectedFares.slice(0, 1) : selectedFares

        const { baseFare, tax, price, amendInsuranceFee, cancellationInsuranceFee, travelInsuranceFee, vdcFee, discountAmount } = faresForBreakup.reduce(
            (acc, fare) => {
                const {
                    totalBaseFare,
                    totalTax,
                    amendInsuranceCharges,
                    cancelInsuranceCharges,
                    travelInsuranceCharges,
                    visaRejectionCoverCharges,
                    discountAmount,
                    displayPrice
                } = getFarePricingAttributes({
                    fare,
                    showPerAdultPricing: false
                })
                return {
                    ...acc,
                    baseFare: totalBaseFare + acc.baseFare,
                    tax: totalTax + acc.tax,
                    price: displayPrice + acc.price,
                    amendInsuranceFee: (amendInsuranceCharges || 0) + acc.amendInsuranceFee,
                    cancellationInsuranceFee: (cancelInsuranceCharges || 0) + acc.cancellationInsuranceFee,
                    travelInsuranceFee: (travelInsuranceCharges || 0) + acc.travelInsuranceFee,
                    vdcFee: (visaRejectionCoverCharges || 0) + acc.vdcFee,
                    discountAmount: (discountAmount || 0) + acc.discountAmount
                }
            },
            { baseFare: 0, tax: 0, price: 0, amendInsuranceFee: 0, cancellationInsuranceFee: 0, travelInsuranceFee: 0, vdcFee: 0, discountAmount: 0 }
        )

        // showPerAdultPricing: false — the tooltip always breaks down the trip total.
        const { nepDetails } = getCouponDisplayAndDiscountValue({
            selectedFares,
            showPerAdultPricing: false,
            isRT,
            couponData,
            cardKey,
            isSpecialRT: effectiveIsSpecialRT
        })

        // NEP prices the itinerary as a whole, so base + taxes + fees need not land on the total.
        const totalPrice = nepDetails.hasNepStrikeOff ? nepDetails.displayPrice : price
        const totalDiscount = nepDetails.hasNepStrikeOff ? getNepDiscountAmount(nepDetails) : discountAmount

        const tooltipContent = (
            <PerAdultTooltip
                amendInsuranceFee={amendInsuranceFee}
                cancellationInsuranceFee={cancellationInsuranceFee}
                discountAmount={totalDiscount}
                paxCriteria={paxCriteria}
                totalBaseFare={baseFare}
                totalPrice={totalPrice}
                totalTax={tax}
                travelInsuranceFee={travelInsuranceFee}
                vdcFee={vdcFee}
            />
        )
        return tooltipContent
    }
    // couponData lands after the modal opens; without it in the deps the tooltip stays pre-NEP.
    const tooltipContent = useMemo(getTooltipContent, [selectedFares, couponData, cardKey, isRT, effectiveIsSpecialRT])

    // Mirror flights-pwa InterstitialPage a_ff_coverage:
    // isFromFf && faresArray.length > 1 && !interstitialDataLoading && selectedLeg === 0
    // && benefits present. Snapshot fares on first paint-ready frame so a later
    // fetch-interstitial-page merge cannot rewrite price / fare fields.
    const ffCoverageFaresRef = useRef<FareType[] | null>(null)
    useEffect(() => {
        if (ffCoverageRef.current || !isFFFlow || selectedTabIndex !== 0) return
        if (interstitialFaresLoading || !isInterstitialDataReady) return
        const onwardFares = listOfFares[0] || []
        if (onwardFares.length <= 1) return
        if (!ffCoverageFaresRef.current) {
            ffCoverageFaresRef.current = onwardFares
        }
        if (isEmpty(servicesData) || isEmpty(baggageAllowances)) return

        const faresForCoverage = ffCoverageFaresRef.current
        const benefits: Record<string, string> = {
            a_ff1_benefits: 'null',
            a_ff2_benefits: 'null',
            a_ff3_benefits: 'null',
            a_ff4_benefits: 'null'
        }
        faresForCoverage.forEach((fareList, index) => {
            if (index > 3 || isEmpty(fareList)) return
            const benefitsProvided = getBenefitProvided(fareList, servicesData, baggageAllowances)
            benefits[`a_ff${index + 1}_benefits`] =
                Object.values(benefitsProvided).filter(Boolean).join(' | ').trim() || 'null'
        })

        ffCoverageRef.current = true

        const flightNumberValue: string[] = []
        const airlineNamesValue: string[] = []
        const ptcFarePresent: string[] = []
        cardDetails?.forEach(card => {
            const { summary, subTravelOptionIds = [] } = card || {}
            const { flights = [] } = summary || {}
            const subtravelOption = subTravelOptions[subTravelOptionIds?.[0] || ''] || {}
            const { fareIds = [] } = subtravelOption || {}
            const ptcFare = getPtcFare(fareIds, fares, 'student') || {}
            ptcFarePresent.push(!isEmpty(ptcFare) ? 'yes' : 'no')
            flights.forEach((flight: any) => {
                flightNumberValue.push(`${flight?.airlineCode}-${flight?.flightNumber}`)
                airlineNamesValue.push(metaData?.airlineDetail?.[flight?.airlineCode]?.name)
            })
        })

        const benefitDetailsCTBranded = getRavenPayloadCTBRAND(faresForCoverage, servicesData, baggageAllowances)
        const ravenPayload = {
            page_name: 'flights_srp',
            a_flight_number: flightNumberValue?.join(',') || 'N/A',
            a_airline: airlineNamesValue?.join(',') || 'N/A',
            a_ff1_benefits: benefits.a_ff1_benefits || 'null',
            a_ff2_benefits: benefits.a_ff2_benefits || 'null',
            a_ff3_benefits: benefits.a_ff3_benefits || 'null',
            a_ff4_benefits: benefits.a_ff4_benefits || 'null',
            a_fare_price: faresForCoverage?.[0]?.pricing?.totalPricing?.totalPrice || 0,
            a_srp_ff_cookie: getFFCookieValue(),
            a_student_fare_family_available: ptcFarePresent?.join(',') || 'N/A',
            ...benefitDetailsCTBranded
        }
        ravenSDKSrpCfwTrigger('a_ff_coverage', searchData, processedData, { ...ravenPayload })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isFFFlow,
        selectedTabIndex,
        listOfFares,
        interstitialFaresLoading,
        isInterstitialDataReady,
        servicesData,
        baggageAllowances
    ])

    // Upgrade CTA (nudge banner / upgrade callout / anchored banner): switch to the
    // callout's targetCabin tab (Indigo Upfront or PE) AND auto-select the cheapest
    // fare in that tab — never hardcode Premium Economy.
    const handlePeUpgrade = () => {
        // Economy → target-cabin upgrade CTA on the SRP fare modal (a_class_upgrade).
        ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, {
            action_name: 'a_class_upgrade',
            action_type: 'click',
            page_name: 'flights_srp',
        })
        // Tab select + fare select (below) suppress the nudge for this search.
        const available = effectiveIsSpecialRT ? specialRtAvailableCabins : activeLegMatrix.availableCabins
        const targetTab =
            resolveUpgradeCalloutTab(peUpgradeOffer?.targetCabin, available) ||
            resolveUpgradeCalloutTab('PREMIUM_ECONOMY', available) ||
            'PREMIUM_ECONOMY'
        handleCabinSelect(targetTab)
        if (effectiveIsSpecialRT) return
        const legFares = listOfFares[selectedTabIndex]
        const cheapestTargetFare = getCheapestFareForCabin(
            legFares,
            targetTab,
            activeLegMatrix.cabinClassSummary,
            showPerAdultPricing
        )
        if (!cheapestTargetFare) return
        const targetFares = filterFaresByCabin(legFares, targetTab, activeLegMatrix.cabinClassSummary)
        const cheapestIndex = targetFares.findIndex(fare => fare.fareId === cheapestTargetFare.fareId)
        selectFare(cheapestTargetFare as FareType, cheapestIndex >= 0 ? cheapestIndex : 0)
    }

    // opening the PE cabin media/video drawer on the SRP fare modal (a_class_image_expand).
    const handleOpenCabinMediaDrawer = () => {
        ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, {
            action_name: 'a_class_image_expand',
            action_type: 'click',
            page_name: 'flights_srp',
        })
        setShowCabinMediaDrawer(true)
    }

    const closeInterstitialAlert = () => setInterstitialAlert(prev => ({ ...prev, isOpen: false }))

    // Accept new fare: prices are already merged into the fare strip; dismiss the
    // dialog and let the user continue with the re-priced fare.
    const acceptInterstitialPriceChange = () => {
        closeInterstitialAlert()
    }

    // Find another flight: dismiss and return the user to the SRP (close the modal).
    const findAnotherFlightFromAlert = () => {
        closeInterstitialAlert()
        closeModal && closeModal()
    }

    const isPriceChangeAlertOpen =
        interstitialAlert.isOpen && interstitialAlert.type !== INTERSTITIAL_ALERT_TYPE.FLIGHT_UNAVAILABLE
    const isFlightUnavailableAlertOpen =
        interstitialAlert.isOpen && interstitialAlert.type === INTERSTITIAL_ALERT_TYPE.FLIGHT_UNAVAILABLE

    // ── RT combined-key WOW callout (matches mobile InterstitialPage RT) ──
    // RT WOW data lives in `wowFares` keyed by `[onwardFareId, returnFareId]`
    // (or `[specialRTFareId]`). Build that key from the user's CURRENT
    // selection across both tabs and pass it as a synthetic fareId so
    // WowFareCallout's internal `wowFares?.[fareId]` lookup hits.
    const rtWowFareKey = isRT
        ? isSpecialRT
            ? `[${selectedFares?.[0]?.fareId}]`
            : `[${selectedFares?.[0]?.fareId}, ${selectedFares?.[1]?.fareId}]`
        : ''
    const rtBothFaresSelected =
        isRT &&
        !!selectedFares?.[0]?.fareId &&
        (isSpecialRT || !!selectedFares?.[1]?.fareId)
    const rtWowListOfFares = rtBothFaresSelected
        ? [
              {
                  fareId: rtWowFareKey,
                  showPerAdultPricing: !!showPerAdultPricing,
                  couponCallout: ''
              }
          ]
        : []

    return (
        <>
        <Container className="flex flex-column p-relative o-visible h-100p min-h-0">
            {/* Only the header carried a radius, so the footer squared off the bottom.
                Round the clipping shell instead so all four corners match. */}
            <Container
                className="o-hidden flex flex-column flex-1 h-100p min-h-0"
                css={{ borderRadius: `${PE_MODAL_BORDER_RADIUS_PX}px` }}
            >
            {showPeInterstitialLayout ? (
                // z-index above the fare scroll: scroll uses marginTop -1px to tuck under
                // the active cabin tab, but must not cover the full-width tab divider.
                <Container className="p-relative" css={{ zIndex: 2 }}>
                    <PEInterstitialHeader
                        caretTabId={captionCaretTabId}
                        headerDetails={headerDetails}
                        isRT={isRT}
                        isSpecialRT={effectiveIsSpecialRT}
                        metaData={metaData}
                        priceByCabin={effectiveIsSpecialRT ? specialRtPriceByCabin : activeLegMatrix.priceByCabin}
                        searchIntent={searchIntent}
                        selectedCabin={effectiveSelectedCabin}
                        selectedTabIndex={selectedTabIndex}
                        setSelectedTabIndex={setSelectedTabIndex}
                        themeCabin={selectedCabinTheme}
                        visibleCabins={visibleCabins}
                        onCabinSelect={handleCabinTabClick}
                        onClose={closeModal}
                        onPeTabRef={handlePeTabRef}
                    />
                    {showPeNudgeBanner && (
                        <PENudgeBanner
                            isExiting={isPeNudgeBannerExiting}
                            peTabAnchorRef={peTabRef}
                            text={caption}
                            variant={upgradeNudgeVariant}
                            onCaretClick={() => handleCabinTabClick(captionCaretTabId as CabinTabId)}
                        />
                    )}
                </Container>
            ) : (
                <FareFamilyHeader
                    headerDetails={headerDetails}
                    isSpecialRT={effectiveIsSpecialRT}
                    selectedTabIndex={selectedTabIndex}
                    setSelectedTabIndex={setSelectedTabIndex}
                />
            )}
            <Container
                className="flex-1 oy-scroll"
                data-interstitial-mode={interstitialView.tripMode}
                data-interstitial-resolved={String(interstitialView.isResolved)}
                data-interstitial-status={interstitialView.status}
                data-testid="interstitial-body"
                style={{
                    width: '1216px',
                    ...(showPeInterstitialLayout
                        ? {
                              marginTop: '-1px',
                              position: 'relative',
                              zIndex: 1,
                              backgroundColor: '#FFFFFF',
                          }
                        : {}),
                }}
            >
                <FareFamilyBlock
                    cabinPromoBannerTitle={
                        (
                            activeLegMatrix.cabinClassSummary?.[effectiveSelectedCabin] ||
                            (selectedCabinTheme
                                ? activeLegMatrix.cabinClassSummary?.[selectedCabinTheme]
                                : undefined)
                        )?.cabinPromoBanner?.title
                    }
                    cardDetails={cardDetails}
                    cardKey={cardKey}
                    couponData={couponData}
                    fares={
                        effectiveIsSpecialRT
                            ? showPeInterstitialLayout && specialRtAvailableCabins.length > 0
                                ? filterFaresByCabin(specialRtAllFares, effectiveSelectedCabin)
                                : specialRtAllFares
                            : showPeInterstitialLayout && activeLegMatrix.hasCabinMatrix
                              ? filterFaresByCabin(
                                    arrangeFaresArray(listOfFares[selectedTabIndex], selectedFares, selectedTabIndex),
                                    effectiveSelectedCabin,
                                    activeLegMatrix.cabinClassSummary
                                )
                              : arrangeFaresArray(
                                    // Bugfix 6 (PE only): once getFaresInfo has real fares, use them; until
                                    listOfFares[selectedTabIndex],
                                    selectedFares,
                                    selectedTabIndex
                                )
                    }
                    faresObj={fares}
                    interstitialFaresLoading={interstitialFaresLoading}
                    isCabinFilteredList={
                        showPeInterstitialLayout &&
                        // Either BE bucketed the strip to the active tab, or this leg
                        // has a single cabin (tabs hidden) so there is no other tab a
                        // card could have leaked in from.
                        (visibleCabins.length <= 1 ||
                            (effectiveIsSpecialRT
                                ? specialRtAvailableCabins.length > 0
                                : activeLegMatrix.hasCabinMatrix))
                    }
                    isEconomySearchFlow={isEconomySearchFlow}
                    isFFFlow={isFFFlow}
                    isIntl={processedData?.isIntl}
                    isRT={isRT}
                    isServicesLoading={isServicesLoading || interstitialFaresLoading}
                    isSpecialRT={effectiveIsSpecialRT}
                    onwardLegCabin={
                        showPeInterstitialLayout && isRT && !!selectedTabIndex && !effectiveIsSpecialRT
                            ? onwardLegCabin
                            : undefined
                    }
                    otherTabSelectedFareId={
                        isRT ? selectedFares?.[selectedTabIndex === 0 ? 1 : 0]?.fareId : undefined
                    }
                    peCarouselImages={peCarouselImages}
                    peUpgradeAmount={
                        iuOffer?.eligible ? iuOffer.amount : peUpgradeOffer?.eligible ? peUpgradeOffer.amount : undefined
                    }
                    peUpgradeImageSrc={
                        isIndigoUpsell || iuOffer
                            ? iuOffer?.mediaUrl || undefined
                            : PE_ITINERARY_UPGRADE_SEAT_IMAGE
                    }
                    peUpgradeTitle={isIndigoUpsell || iuOffer ? iuOffer?.title : undefined}
                    peUpgradeVariant={upgradeNudgeVariant}
                    peUpgradeVideoSrc={isIndigoUpsell ? undefined : CABIN_HERO_VIDEOS.premium}
                    retryCountForServices={retryCountForServices}
                    selectedCabin={showPeInterstitialLayout ? effectiveSelectedCabin : undefined}
                    selectedFare={selectedFares[selectedTabIndex]}
                    selectedFaresObj={selectedFares}
                    selectedTabIndex={selectedTabIndex}
                    selectFare={selectFare}
                    servicesData={servicesData}
                    showPeInterstitialLayout={showPeInterstitialLayout}
                    showPerAdultPricing={showPerAdultPricing}
                    showPeUpgradeCallout={showPeUpgradeCallout}
                    showVideoModal={handleOpenCabinMediaDrawer}
                    subTravelOptions={subTravelOptions}
                    themeCabin={showPeInterstitialLayout ? selectedCabinTheme : undefined}
                    totalFareCount={
                        effectiveIsSpecialRT
                            ? showPeInterstitialLayout
                                ? specialRtTotalFareCount
                                : subTravelOptions[cardDetails[selectedTabIndex].travelOptionId].totalFareCount
                            : showPeInterstitialLayout
                              ? activeLegMatrix.hasCabinMatrix
                                  ? activeLegMatrix.fareCountByCabin[effectiveSelectedCabin]
                                  : activeLegTotalFareCount
                              : subTravelOptions[cardDetails[selectedTabIndex].travelOptionId].totalFareCount
                    }
                    onOpenVdcEducation={() => setShowVdcEducation(true)}
                    onPeUpgrade={handlePeUpgrade}
                    onRetryForBenefits={onRetryForBenefits}
                />
            </Container>
            <FareFamilyFooter
                baggageAllowances={baggageAllowances}
                cardDetails={cardDetails}
                cardKey={cardKey}
                couponData={couponData}
                dataId={dataId}
                fares={fares}
                isFFFlow={isFFFlow}
                isRT={isRT}
                isSpecialRT={effectiveIsSpecialRT}
                listOfFares={listOfFares}
                processedData={processedData}
                searchData={searchData}
                selectedFareIndex={selectedFareIndex}
                selectedFares={selectedFares}
                selectedFaresObj={selectedFares}
                selectedTabIndex={selectedTabIndex}
                servicesData={servicesData}
                setSelectedTabIndex={setSelectedTabIndex}
                showPeInterstitialLayout={showPeInterstitialLayout}
                showPerAdultPricing={showPerAdultPricing}
                tooltipContent={tooltipContent}
                tuppleIndex={tuppleIndex}
            />
            </Container>
            </Container>
            <Modal
            allowBackdropClose
            hideCrossIcon={false}
            modalContentCss={{
                width: '100%',
                borderRadius: '32px 32px 0 0',
                position: 'absolute',
                height: '93vh',
                bottom: '0px',
            }}
            open={showVdcEducation}
            onClose={() => setShowVdcEducation(false)}
        >
            <VdcEducationPage onClose={() => setShowVdcEducation(false)} />
        </Modal>
        <CabinMediaDrawer
            isOpen={showCabinMediaDrawer}
            resources={galleryResources}
            onClose={() => setShowCabinMediaDrawer(false)}
        />
        <Modal
            allowBackdropClose={false}
            hideCrossIcon={false}
            modalContentCss={{ width: '400px', borderRadius: '16px', padding: 0, zIndex: 1300 }}
            open={isPriceChangeAlertOpen}
            onClose={findAnotherFlightFromAlert}
        >
            <PriceChangeAlert
                newPrice={interstitialAlert.newPrice || ''}
                priceDelta={interstitialAlert.priceDelta || ''}
                title={interstitialAlert.title}
                variant={
                    interstitialAlert.variant ||
                    (interstitialAlert.type === INTERSTITIAL_ALERT_TYPE.PRICE_INCREASE
                        ? PRICE_CHANGE_VARIANT.INCREASE
                        : PRICE_CHANGE_VARIANT.DROP)
                }
                onAcceptNewFare={acceptInterstitialPriceChange}
                onFindAnotherFlight={findAnotherFlightFromAlert}
            />
        </Modal>
        <Modal
            allowBackdropClose={false}
            hideCrossIcon={false}
            modalContentCss={{ width: '400px', borderRadius: '16px', padding: 0 }}
            open={isFlightUnavailableAlertOpen}
            onClose={findAnotherFlightFromAlert}
        >
            <FlightUnavailableAlert
                subTitle={interstitialAlert.subTitle}
                title={interstitialAlert.title}
                onFindAnotherFlight={findAnotherFlightFromAlert}
            />
        </Modal>
        </>
    )
}

export default FareFamilyModal
